import React from "react";
import styled, { keyframes } from "styled-components";
import ScrollArea from "react-scrollbar";
import { useLocation } from "react-router-dom";
import { StyledH1 } from "@aureskonnect/react-ui";
import { StyledLabel } from "@aureskonnect/react-ui";
import { useSelector } from "react-redux";
import { Menu } from "./Menu";

import "bootstrap/dist/css/bootstrap.min.css";
import "./sidebar.css";
// eslint-disable-next-line react-hooks/rules-of-hooks

export type MenuItemsType = {
    menuItem: string;
    link: string;
    icon?: any;
    subMenu?: MenuItemsType[];
    customClassName: string;
    id: string;
};

export type StyledSidebarMenuPropsType = {
    brand?: React.ReactNode;
    name?: string;
    menuItems: MenuItemsType[];
    backgroundColor?: string;
    collapsed?: boolean;
};

// @see https://www.typescriptlang.org/docs/handbook/utility-types.html#picktype-keys
// docs above uses `interface` keyword to define types,
// which is `equivalent` to the `type` keyword
type WrapperPropsType = Pick<
    StyledSidebarMenuPropsType,
    "backgroundColor" | "collapsed"
> & {
    mainColor?: string;
};

// Animations
const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateX(-10px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
`;

const shimmer = keyframes`
    0% {
        background-position: -200% 0;
    }
    100% {
        background-position: 200% 0;
    }
`;

const bounce = keyframes`
    0%, 100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-3px);
    }
`;

const glow = keyframes`
    0%, 100% {
        box-shadow: 0 0 5px rgba(255, 255, 255, 0.3);
    }
    50% {
        box-shadow: 0 0 15px rgba(255, 255, 255, 0.6);
    }
`;

const slideIn = keyframes`
    from {
        opacity: 0;
        transform: translateX(-20px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
`;

const ripple = keyframes`
    0% {
        transform: scale(0);
        opacity: 0.5;
    }
    100% {
        transform: scale(4);
        opacity: 0;
    }
`;

const Wrapper = styled.div<WrapperPropsType>`
    display: flex;
    flex-direction: column;
    ${({ collapsed }: WrapperPropsType) => {
        return collapsed ? "width:71px" : "width: 274px";
    }};
    min-height: 100vh;
    border-right: none;
    box-shadow: none;
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;

    .brand__clz {
        ${({ collapsed }: WrapperPropsType) => {
            return collapsed ? "width: 73px;height: 36px;" : "";
        }};
        transition: all 0.3s ease;
    }

    .sidebar-menu__clz {
        position: relative;
        z-index: 2;
    }

    .sidebar-menu__clz a {
        display: flex;
        align-items: center;
        padding: 0.75rem 1.25rem;
        text-decoration: none;
        color: #ffffff;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;
    }

    .sidebar-menu__clz a::before {
        content: "";
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        width: 200%;
        background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.15) 50%,
            transparent 100%
        );
        background-size: 200% 100%;
        opacity: 0;
        transition: opacity 0.3s ease;
    }

    .sidebar-menu__clz a:hover::before {
        opacity: 1;
        animation: ${shimmer} 1.5s infinite;
    }

    .sidebar-menu__clz,
    .sidebar-menu__clz ul {
        padding: 0;
        margin: 0;
        list-style: none;
        color: #ffffff;
    }

    .sidebar-menu__clz .active {
        background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.25) 0%,
            rgba(255, 255, 255, 0.1) 100%
        );
        border-left: 4px solid white;
        color: white;
    }

    .sidebar-menu__clz a:hover {
        color: white;
        opacity: 1;
        padding-left: 1.5rem;
    }

    .sidebar-menu__clz a {
        opacity: 0.85;
    }

    .sidebar-menu__clz a:active {
        background-color: rgba(255, 255, 255, 0.3);
        border-left: 4px solid white;
        opacity: 1;
    }

    .sidebar-menu__clz ul a {
        padding-left: 2.5rem;
        font-size: 0.9rem;
    }

    .sidebar-menu__clz li a:hover {
        background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.15) 0%,
            transparent 100%
        );
        border-left: 4px solid rgba(255, 255, 255, 0.7);
    }

    .sidebar-menu__clz .mm-active li:hover {
        background: rgba(255, 255, 255, 0.15);
        ${({ collapsed }: WrapperPropsType) => {
            return collapsed ? " " : "border-left: 4px solid white;";
        }};
    }

    .menuitem-collapsed-header__clz {
        padding: 12px 20px;
        background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.95) 0%,
            rgba(255, 255, 255, 0.85) 100%
        );
        font-weight: 600;
        color: #333;
        border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        text-transform: uppercase;
        font-size: 0.75rem;
        letter-spacing: 0.5px;
    }

    .arrow {
        border: solid white;
        border-width: 0 2px 2px 0;
        padding: 3px;
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .arrowRight {
        transform: rotate(46deg) translateY(-50%);
    }

    .arrowLeft {
        transform: rotate(-135deg) translateY(-50%);
    }

    .sidebar-menu__clz li {
        display: block;
        position: relative;
        line-height: 27px;
        text-align: left;
        //animation: ${fadeIn} 0.4s ease-out forwards;
    }

    .sidebar-menu__clz > li:nth-child(1) {
        animation-delay: 0.1s;
    }
    .sidebar-menu__clz > li:nth-child(2) {
        animation-delay: 0.15s;
    }
    .sidebar-menu__clz > li:nth-child(3) {
        animation-delay: 0.2s;
    }

    .sidebar-menu__clz li ul.collapsed-submenu__clz {
        display: none;
        z-index: 1000;
        position: fixed;
        margin-left: 71px;
        min-width: 240px;
        max-width: 280px;
        margin-top: -43px;
        border-radius: 8px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
        overflow: hidden;
        animation: ${fadeIn} 0.2s ease-out;
    }

    .sidebar-menu__clz li ul.collapsed-submenu__clz li {
        display: block;
    }

    .sidebar-menu__clz li:hover ul.collapsed-submenu__clz {
        display: block;
    }

    /* Submenu items stagger animation */
    .sidebar-menu__clz ul li {
        opacity: 0;
        animation: ${fadeIn} 0.3s ease-out forwards;
    }

    .sidebar-menu__clz ul li:nth-child(1) {
        animation-delay: 0.05s;
    }
    .sidebar-menu__clz ul li:nth-child(2) {
        animation-delay: 0.1s;
    }
    .sidebar-menu__clz ul li:nth-child(3) {
        animation-delay: 0.15s;
    }
    .sidebar-menu__clz ul li:nth-child(4) {
        animation-delay: 0.2s;
    }
    .sidebar-menu__clz ul li:nth-child(5) {
        animation-delay: 0.25s;
    }
    .sidebar-menu__clz ul li:nth-child(6) {
        animation-delay: 0.3s;
    }
    .sidebar-menu__clz ul li:nth-child(7) {
        animation-delay: 0.35s;
    }
    .sidebar-menu__clz ul li:nth-child(8) {
        animation-delay: 0.4s;
    }
    .sidebar-menu__clz ul li:nth-child(9) {
        animation-delay: 0.45s;
    }
    .sidebar-menu__clz ul li:nth-child(10) {
        animation-delay: 0.5s;
    }
    .sidebar-menu__clz ul li:nth-child(11) {
        animation-delay: 0.55s;
    }
    .sidebar-menu__clz ul li:nth-child(12) {
        animation-delay: 0.6s;
    }
    .sidebar-menu__clz ul li:nth-child(13) {
        animation-delay: 0.65s;
    }

    /* Icon hover effect */
    .sidebar-menu__clz li a svg {
        transition: transform 0.3s ease, filter 0.3s ease;
    }

    .sidebar-menu__clz li a:hover svg {
        animation: ${bounce} 0.5s ease;
        filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.6));
    }

    /* Effet ripple au clic */
    .sidebar-menu__clz a::after {
        content: "";
        position: absolute;
        width: 20px;
        height: 20px;
        background: rgba(255, 255, 255, 0.4);
        border-radius: 50%;
        transform: scale(0);
        pointer-events: none;
    }

    .sidebar-menu__clz a:active::after {
        animation: ${ripple} 0.6s ease-out;
    }

    /* Effet glow sur l'élément actif */
    .sidebar-menu__clz .active {
        animation: ${glow} 2s ease-in-out infinite;
    }

    /* Animation slideIn pour les sous-menus */
    .sidebar-menu__clz ul.mm-collapse.mm-show li {
        animation: ${slideIn} 0.3s ease-out forwards;
    }

    /* Scrollbar styling */
    .scrollarea-content {
        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.1);
        }

        &::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.3);
            border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.5);
        }
    }
`;
export function getAuthorizationStates(id: string, authorizationList: any) {
    if (
        process.env.REACT_APP_Authorization !== undefined &&
        JSON.parse(process.env.REACT_APP_Authorization) === false
    ) {
        return {
            Consult: true,
            Modifie: true,
            Visible: true,
        };
    }

    let item: any = {
        Consult: false,
        Modifie: false,
        Visible: false,
    };

    if (authorizationList === undefined || authorizationList === null) {
        return {
            Consult: false,
            Modifie: false,
            Visible: false,
        };
    } else {
        for (let e in authorizationList) {
            if (e === id) {
                item = authorizationList[e];
            }
        }
        if (id === "idCompte") {
            return {
                Consult: true,
                Modifie: true,
                Visible: true,
            };
        } else {
            return {
                Consult: item?.Consult !== undefined ? item?.Consult : false,
                Modifie: item?.Modifie !== undefined ? item?.Modifie : false,
                Visible: item?.Visible !== undefined ? item?.Visible : false,
            };
        }
    }
}
export function isVisibleElement(id: string, authorizationList: any) {
    let authorization = getAuthorizationStates(id, authorizationList);
    return authorization.Visible;
}
export function StyledSidebarMenu({
    brand,
    name,
    menuItems,
    backgroundColor,
    collapsed,
}: StyledSidebarMenuPropsType) {
    const location = useLocation();
    const mainColor = useSelector(
        (state: any) => state.global.infoUser.maincolor
    );

    const [menuCollapsed, setMenuCollapsed] = React.useState<number>(-1);
    const [subMenuCollapsed, setSubMenuCollapsed] = React.useState<number>(-1);
    const [isMenuClicked, setIsMenuClicked] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (menuItems !== undefined && isMenuClicked === false) {
            menuItems!.forEach((el: any, index: number) => {
                if (el.link === location.pathname) {
                    setMenuCollapsed!(index);
                }

                el.subMenu.forEach((elem: any, key: number) => {
                    if (elem.link === location.pathname) {
                        setSubMenuCollapsed!(key);
                        setMenuCollapsed!(index);
                    }
                    if (elem.subMenu !== undefined) {
                        elem.subMenu.forEach((item: any, ref: number) => {
                            if (item.link === location.pathname) {
                                setSubMenuCollapsed!(key);
                                setMenuCollapsed!(index);
                            }
                        });
                    }
                });
            });
            setIsMenuClicked(false);
        }
        //eslint-disable-next-line
    }, [location]);

    React.useEffect(() => {
        setIsMenuClicked(false);
    }, [location]);

    return (
        <Wrapper
            backgroundColor={backgroundColor}
            collapsed={collapsed}
            style={{ backgroundColor: mainColor }}
        >
            {brand ? (
                <StyledH1 className="mb-5 mt-2 text-white d-flex justify-content-center brand__clz">
                    {typeof brand === "string" && collapsed === true
                        ? brand.slice(0, 2)
                        : brand}
                    *
                </StyledH1>
            ) : null}
            {name ? (
                <StyledLabel
                    className="ml-3 text-white"
                    style={{
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        letterSpacing: "1.5px",
                        textTransform: "uppercase",
                        marginTop: "15px",
                        marginBottom: "10px",
                        opacity: 0.9,
                    }}
                >
                    {name}
                </StyledLabel>
            ) : null}

            <ScrollArea
                className="sidebar-menu__clz content"
                style={{ width: "auto", height: "89vh" }}
                horizontal={true}
                speed={1.0}
            >
                {menuItems.map(
                    (
                        {
                            link,
                            menuItem,
                            subMenu,
                            icon,
                            customClassName,
                            id,
                        }: MenuItemsType,
                        index: number
                    ) => {
                        return (
                            <Menu
                                setSubMenuCollapsed={setSubMenuCollapsed}
                                subMenuCollapsed={subMenuCollapsed}
                                setIsMenuClicked={setIsMenuClicked}
                                setMenuCollapsed={setMenuCollapsed}
                                menuCollapsed={menuCollapsed}
                                key={index}
                                link={link}
                                menuItem={menuItem}
                                icon={icon}
                                subMenu={subMenu}
                                index={index}
                                collapsed={collapsed}
                                backgroundColor={backgroundColor}
                                customClassName={customClassName}
                                id={id}
                            />
                        );
                    }
                )}
            </ScrollArea>
        </Wrapper>
    );
}
